"use client";

import { useRef, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import CampaignOverviewMap from "@/components/maps/CampaignOverviewMap";
import { clientLogger } from "@/lib/client-logger";
import { DEBUG_FLAGS } from "@/lib/debug-config";
import { isWhitelabelMode } from "@/lib/whitelabel-config";
import { getDisplayStatus, getStatusColor } from "@/lib/campaign-status";
import { ClueWrapper as ClueClient } from "@/components/clue/ClueWrapper";

interface Campaign {
  id: string;
  title: string;
  slug: string;
  description: string;
  orgDescription?: string;
  locationText?: string;
  coverImageUrl?: string;
  status: string;
  startAt: string | null;
  endAt: string | null;
  createdAt: string;
  participantCap?: number;
  mintCap?: number;
  isPublic: boolean;
  showMap?: boolean;
  participantCount: number;
  campaignType?: string;
  chain?: string;
  authMode?: string;
  latitude?: number;
  longitude?: number;
}

interface Tenant {
  name: string;
  slug: string;
  website?: string;
  logoUrl?: string;
  thumbnailLogoUrl?: string;
  primaryColor: string;
  secondaryColor: string;
}

interface Clue {
  id: string;
  title: string;
  description: string;
  address: string;
  latitude?: number;
  longitude?: number;
  radiusMiles?: number;
  hideAddress?: boolean;
  slug: string;
  orderIndex: number;
  imageUrl?: string | null;
  atomicTemplateId?: string | null;
  poapEventId?: string | null;
  assetImage?: string | null;
  assetVideo?: string | null;
  assetName?: string | null;
}

interface CampaignDetailClientProps {
  campaign: Campaign;
  tenant: Tenant;
  clues: Clue[];
  serverSearchParams?: { [key: string]: string | string[] | undefined };
}

export function CampaignDetailClient({ campaign, tenant, clues, serverSearchParams }: CampaignDetailClientProps) {
  const claimRef = useRef<HTMLDivElement>(null);
  const hasIykRef = !!(serverSearchParams?.iykRef || serverSearchParams?.iykref);

  useEffect(() => {
    if (hasIykRef && campaign.campaignType === 'SINGLE_LOCATION') {
      const scrollToClaim = () => {
        const el = document.getElementById('claim-section');
        if (el) {
          const navHeight = 64;
          const y = el.getBoundingClientRect().top + window.scrollY - navHeight;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      };
      // Try immediately, then retry after hydration settles
      scrollToClaim();
      setTimeout(scrollToClaim, 300);
      setTimeout(scrollToClaim, 1000);
    }
  }, [hasIykRef, campaign.campaignType]);
  const isSingleLocation = campaign.campaignType === 'SINGLE_LOCATION';
  const singleClue = isSingleLocation ? clues[0] : null;

  clientLogger.debug("CampaignDetailClient rendering", { campaign, tenant, clues });

  // Add a simple test div at the very top
  if (typeof window !== 'undefined') {
    clientLogger.debug("Client-side rendering confirmed");
  }

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isActive = () => {
    const now = new Date();
    const start = campaign.startAt ? new Date(campaign.startAt) : null;
    const end = campaign.endAt ? new Date(campaign.endAt) : null;
    
    return campaign.status === "ACTIVE" || 
           (campaign.status === "PUBLISHED" && 
            (!start || start <= now) && 
            (!end || end >= now));
  };

  const canParticipate = isActive();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Debug: Component test banner */}
      {DEBUG_FLAGS.showMapDebugInfo && (
        <div className="bg-yellow-300 text-black p-4 text-center font-bold">
          🧪 COMPONENT TEST: CampaignDetailClient is rendering! Campaign: {campaign.title}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm text-gray-500">
            {!isWhitelabelMode() && (
              <>
                <li>
                  <Link href="/browse" className="hover:text-gray-700">
                    Browse
                  </Link>
                </li>
                <li>→</li>
              </>
            )}
            <li>
              <Link href={`/${tenant.slug}`} className="hover:text-gray-700">
                {tenant.name}
              </Link>
            </li>
            <li>→</li>
            <li className="text-gray-900">{campaign.title}</li>
          </ol>
        </nav>

        {/* Cover Image or Placeholder */}
        {campaign.coverImageUrl ? (
          <div
            className="w-full aspect-[16/5.4] mb-8 rounded-lg overflow-hidden bg-gray-100"
            style={{
              backgroundImage: `url(${campaign.coverImageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
            role="img"
            aria-label={campaign.title}
          />
        ) : (
          <div className="w-full aspect-[16/5.4] mb-8 rounded-lg overflow-hidden flex items-center justify-center bg-gray-100">
            <div className="text-gray-400 text-center">
              <svg
                className="w-12 h-12 mx-auto mb-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <p className="text-sm text-gray-500">No cover image</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center space-x-4">
              {(tenant.thumbnailLogoUrl || tenant.logoUrl) && (
                <div className="relative w-16 h-16">
                  <Image
                    src={tenant.thumbnailLogoUrl || tenant.logoUrl!}
                    alt={tenant.name}
                    fill
                    className="object-contain"
                    unoptimized
                  />
                </div>
              )}
              <div>
                <h2 className="text-lg font-medium text-gray-900">{tenant.name}</h2>
                <p className="text-gray-600">by {tenant.name}</p>
              </div>
            </div>
            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(getDisplayStatus(campaign))}`}>
              {getDisplayStatus(campaign)}
            </span>
          </div>

          {isSingleLocation && singleClue ? (
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">
                  {campaign.title}
                </h1>
                <p className="text-xl text-gray-600 whitespace-pre-line">
                  {campaign.description}
                </p>
              </div>
              <div id="claim-section" ref={claimRef} className="lg:w-[380px] flex-shrink-0">
                <ClueClient
                  clue={{
                    id: singleClue.id,
                    title: campaign.title,
                    description: campaign.description,
                    slug: singleClue.slug,
                    address: singleClue.address || campaign.locationText || '',
                    latitude: singleClue.latitude,
                    longitude: singleClue.longitude,
                    radiusMiles: singleClue.radiusMiles,
                    hideAddress: singleClue.hideAddress ?? false,
                    showMap: false,
                    orderIndex: singleClue.orderIndex,
                    assetMetadata: null,
                    atomicTemplateId: singleClue.atomicTemplateId || undefined,
                    assetImage: singleClue.assetImage || undefined,
                    assetVideo: singleClue.assetVideo || undefined,
                    assetName: singleClue.assetName || campaign.title,
                    imageUrl: singleClue.imageUrl,
                  }}
                  campaign={{
                    id: campaign.id,
                    title: campaign.title,
                    slug: campaign.slug,
                    orgName: tenant.name,
                    chain: campaign.chain || 'WAX',
                    showMap: false,
                    campaignType: 'SINGLE_LOCATION',
                    authMode: campaign.authMode,
                  }}
                  tenant={{
                    slug: tenant.slug,
                    name: tenant.name,
                    primaryColor: tenant.primaryColor,
                    logoUrl: tenant.logoUrl || undefined,
                  }}
                  navigation={{
                    prev: { slug: singleClue.slug, title: campaign.title },
                    next: { slug: singleClue.slug, title: campaign.title },
                  }}
                  searchParams={serverSearchParams || {}}
                  routePattern="tenant-campaign"
                />
              </div>
            </div>
          ) : (
            <>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                {campaign.title}
              </h1>
              <p className="text-xl text-gray-600 mb-8 whitespace-pre-line">
                {campaign.description}
              </p>
              <div className="flex gap-4 mb-8">
                {canParticipate ? (
                  <Link
                    href={`/${tenant.slug}/campaigns/${campaign.slug}/locations`}
                    className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                    style={isWhitelabelMode() ? {
                      backgroundColor: tenant.primaryColor || undefined
                    } : undefined}
                  >
                    Start Campaign
                  </Link>
                ) : (
                  <Link
                    href={`/${tenant.slug}`}
                    className="text-white px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition-colors bg-gray-500"
                  >
                    View Organization
                  </Link>
                )}
              </div>
            </>
          )}

        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Campaign Details */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold mb-6 text-gray-900" style={isWhitelabelMode() ? { color: tenant.primaryColor || '#111827' } : undefined}>Campaign Details</h2>

              <div className="space-y-4">
                {campaign.orgDescription && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">About the Organization</h3>
                    <p className="text-gray-600">{campaign.orgDescription}</p>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Start Date</h3>
                    <p className="text-gray-600">{formatDateTime(campaign.startAt)}</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">End Date</h3>
                    <p className="text-gray-600">{formatDateTime(campaign.endAt)}</p>
                  </div>
                </div>

                {campaign.locationText && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Location</h3>
                    <p className="text-gray-600 flex items-center">
                      <span className="mr-2">📍</span>
                      {campaign.locationText}
                    </p>

                    {campaign.showMap && (() => {
                      const mapLocations = isSingleLocation && campaign.latitude && campaign.longitude
                        ? [{
                            id: campaign.id,
                            title: campaign.title,
                            address: campaign.locationText,
                            latitude: campaign.latitude,
                            longitude: campaign.longitude,
                          }]
                        : clues
                            .filter(clue => clue.latitude && clue.longitude)
                            .map(clue => ({
                              id: clue.id,
                              title: clue.title,
                              address: clue.hideAddress ? undefined : clue.address,
                              latitude: clue.latitude!,
                              longitude: clue.longitude!,
                              radiusMiles: clue.radiusMiles,
                              hideAddress: clue.hideAddress,
                            }));

                      return mapLocations.length > 0 ? (
                        <div className="mt-3 rounded-lg overflow-hidden">
                          <CampaignOverviewMap
                            locations={mapLocations}
                            height="300px"
                          />
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}
              </div>

            </div>

          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Locations - hidden for single-location campaigns */}
            {!isSingleLocation && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900" style={isWhitelabelMode() ? { color: tenant.primaryColor || '#111827' } : undefined}>Locations ({clues.length})</h2>
              </div>
              <div className="space-y-3">
                {clues.map((clue, index) => (
                  <Link
                    key={clue.id}
                    href={`/${tenant.slug}/campaigns/${campaign.slug}/locations/${clue.slug}`}
                    className={`block border rounded-lg p-3 hover:shadow-md transition-all cursor-pointer ${
                      canParticipate 
                        ? "hover:bg-gray-50" 
                        : "bg-gray-50 opacity-75 hover:opacity-90"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <span className={`text-xs font-medium px-2 py-1 rounded mr-2 ${
                          canParticipate 
                            ? "bg-gray-100 text-gray-800" 
                            : "bg-gray-200 text-gray-600"
                        }`}>
                          {index + 1}
                        </span>
                        <h3 className={`font-medium text-sm ${
                          canParticipate ? "text-gray-900" : "text-gray-700"
                        }`}>
                          {clue.title}
                        </h3>
                      </div>
                      <svg className={`w-4 h-4 ${
                        canParticipate ? "text-gray-400" : "text-gray-300"
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                    {clue.imageUrl && (
                      <div className="mb-2 rounded overflow-hidden">
                        <Image
                          src={clue.imageUrl}
                          alt={clue.title}
                          width={400}
                          height={200}
                          className="w-full h-32 object-cover"
                          unoptimized
                        />
                      </div>
                    )}
                    <p className={`text-xs mb-1 ${
                      canParticipate ? "text-gray-600" : "text-gray-500"
                    }`}>
                      {clue.description}
                    </p>
                    <p className={`text-xs flex items-center ${
                      canParticipate ? "text-gray-500" : "text-gray-400"
                    }`}>
                      <span className="mr-1">📍</span>
                      {clue.hideAddress && clue.radiusMiles
                        ? `Within ${clue.radiusMiles} mile${clue.radiusMiles !== 1 ? 's' : ''} of this area`
                        : clue.address || 'Location not specified'
                      }
                    </p>
                  </Link>
                ))}
              </div>
              
              {canParticipate && (
                <div className="mt-4">
                  <Link
                    href={`/${tenant.slug}/campaigns/${campaign.slug}/locations`}
                    className="w-full bg-blue-600 text-white text-center py-2 px-4 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors block"
                    style={isWhitelabelMode() ? {
                      backgroundColor: tenant.primaryColor || undefined
                    } : undefined}
                  >
                    View All Locations
                  </Link>
                </div>
              )}
            </div>
            )}

            {/* Organization Info */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold mb-4 text-gray-900" style={isWhitelabelMode() ? { color: tenant.primaryColor || '#111827' } : undefined}>Organization</h2>
              <div className="flex items-center space-x-3 mb-4">
                {(tenant.thumbnailLogoUrl || tenant.logoUrl) && (
                  <div className="relative w-12 h-12">
                    <Image
                      src={tenant.thumbnailLogoUrl || tenant.logoUrl!}
                      alt={tenant.name}
                      fill
                      className="object-contain"
                      unoptimized
                    />
                  </div>
                )}
                <div>
                  <h3 className="font-medium text-gray-900">{tenant.name}</h3>
                  <p className="text-sm text-gray-600">{tenant.name}</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <Link
                  href={`/${tenant.slug}`}
                  className="block w-full bg-gray-100 text-gray-800 text-center py-2 px-4 rounded-md text-sm font-medium hover:bg-gray-200 transition-colors"
                >
                  View Organization
                </Link>
                
                {tenant.website && (
                  <a
                    href={tenant.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full border border-gray-300 text-gray-700 text-center py-2 px-4 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors"
                  >
                    Visit Website
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
